import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, X, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import ServiceIcon, { ICON_OPTIONS } from "@/components/ServiceIcon";

const EMPTY = { name: "", description: "", icon: "sprout", image_url: "", starting_price: "", is_active: true, order: 0 };

export default function ServicesManage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null); // service object or null
  const { data: services = [], isLoading } = useQuery({
    queryKey: ["admin-services"],
    queryFn: async () => (await api.get("/admin/services")).data,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["admin-services"] });
    qc.invalidateQueries({ queryKey: ["services"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    await api.delete(`/admin/services/${id}`);
    refresh();
    toast.success("Service deleted");
  };

  const toggleActive = async (s) => {
    await api.put(`/admin/services/${s.id}`, { ...s, is_active: !s.is_active });
    refresh();
  };

  return (
    <div data-testid="services-manage-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-black text-slate-900 text-2xl sm:text-3xl">Services</h1>
          <p className="text-slate-500 mt-1">Add, edit and price your services.</p>
        </div>
        <button onClick={() => setEditing({ ...EMPTY, order: services.length })} data-testid="service-add-btn" className="inline-flex items-center gap-2 rounded-xl bg-green-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-green-700">
          <Plus className="h-4.5 w-4.5" /> <span className="hidden sm:inline">Add service</span>
        </button>
      </div>

      {isLoading ? <p className="mt-6 text-slate-500">Loading…</p> : (
        <div className="mt-6 space-y-3">
          {services.map((s) => (
            <div key={s.id} className="flex items-center gap-4 rounded-2xl bg-white border border-slate-200 p-4 shadow-sm" data-testid={`service-item-${s.id}`}>
              <div className="h-11 w-11 rounded-xl bg-green-50 text-green-600 grid place-items-center shrink-0"><ServiceIcon name={s.icon} /></div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-slate-900 flex items-center gap-2">{s.name} {!s.is_active && <span className="rounded bg-slate-200 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-500">Inactive</span>}</div>
                <div className="text-sm text-slate-500 truncate">{s.starting_price} · {s.description}</div>
              </div>
              <button onClick={() => toggleActive(s)} data-testid={`service-toggle-${s.id}`} className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${s.is_active ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-500"}`}>{s.is_active ? "Active" : "Inactive"}</button>
              <button onClick={() => setEditing(s)} data-testid={`service-edit-${s.id}`} className="shrink-0 h-9 w-9 rounded-lg bg-slate-100 grid place-items-center hover:bg-slate-200"><Pencil className="h-4 w-4 text-slate-600" /></button>
              <button onClick={() => remove(s.id)} data-testid={`service-delete-${s.id}`} className="shrink-0 h-9 w-9 rounded-lg bg-red-50 grid place-items-center hover:bg-red-100"><Trash2 className="h-4 w-4 text-red-600" /></button>
            </div>
          ))}
        </div>
      )}

      {editing && <ServiceModal service={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />}
    </div>
  );
}

function ServiceModal({ service, onClose, onSaved }) {
  const [form, setForm] = useState(service);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const isNew = !service.id;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image_url", data.url);
      toast.success("Image uploaded");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    setSaving(true);
    const payload = { name: form.name, description: form.description, icon: form.icon, image_url: form.image_url, starting_price: form.starting_price, is_active: form.is_active, order: Number(form.order) || 0 };
    try {
      if (isNew) await api.post("/admin/services", payload);
      else await api.put(`/admin/services/${form.id}`, payload);
      toast.success(isNew ? "Service added" : "Service updated");
      onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const inputCls = "w-full rounded-xl border border-slate-300 px-4 py-2.5 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100";

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="service-modal">
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display font-bold text-xl text-slate-900">{isNew ? "Add service" : "Edit service"}</h2>
          <button onClick={onClose} className="h-9 w-9 rounded-lg bg-slate-100 grid place-items-center"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-4">
          <div><label className="block text-sm font-semibold text-slate-700 mb-1.5">Name</label><input className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="service-form-name" /></div>
          <div><label className="block text-sm font-semibold text-slate-700 mb-1.5">Description</label><textarea rows={3} className={inputCls} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="service-form-desc" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-semibold text-slate-700 mb-1.5">Starting price</label><input className={inputCls} value={form.starting_price} onChange={(e) => set("starting_price", e.target.value)} placeholder="from £75" data-testid="service-form-price" /></div>
            <div><label className="block text-sm font-semibold text-slate-700 mb-1.5">Order</label><input type="number" className={inputCls} value={form.order} onChange={(e) => set("order", e.target.value)} data-testid="service-form-order" /></div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Icon</label>
            <div className="flex flex-wrap gap-2">
              {ICON_OPTIONS.map((ic) => (
                <button key={ic} type="button" onClick={() => set("icon", ic)} className={`h-11 w-11 rounded-xl grid place-items-center border-2 ${form.icon === ic ? "border-green-500 bg-green-50 text-green-600" : "border-slate-200 text-slate-500"}`} data-testid={`icon-${ic}`}>
                  <ServiceIcon name={ic} className="h-5 w-5" />
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Photo (optional)</label>
            <label className="flex items-center gap-3 rounded-xl border border-dashed border-slate-300 px-4 py-3 cursor-pointer hover:border-green-400">
              {uploading ? <Loader2 className="h-5 w-5 animate-spin text-green-600" /> : <Upload className="h-5 w-5 text-slate-400" />}
              <span className="text-sm text-slate-600">{form.image_url ? "Uploaded ✓ — replace" : "Upload photo"}</span>
              <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="service-form-image" />
            </label>
            {form.image_url && <img src={fileUrl(form.image_url)} alt="" className="mt-2 h-24 rounded-lg object-cover" />}
          </div>
          <label className="flex items-center gap-3"><input type="checkbox" checked={form.is_active} onChange={(e) => set("is_active", e.target.checked)} className="h-4 w-4 accent-green-600" data-testid="service-form-active" /><span className="text-sm font-semibold text-slate-700">Active (visible on site)</span></label>
        </div>
        <button onClick={save} disabled={saving} className="mt-6 w-full rounded-xl bg-green-600 py-3 font-bold text-white hover:bg-green-700 disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="service-form-save">
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save service"}
        </button>
      </div>
    </div>
  );
}
